/**
 * 조직관리 메뉴 접근 정책 타입 정의
 * V136: 직책 레벨 기반 메뉴 접근 제한
 * V136.1: jobTitleId → master_jobTitles.code 기반 레벨 판별로 변경
 *         (보안 강화: 기본값 MEMBER로 최소 권한 적용)
 *
 * Firestore 컬렉션: orgManagementPolicies
 * 문서 ID: default
 */

/**
 * 직책 레벨 enum
 *
 * 레벨 순서 (낮을수록 권한 높음):
 * - MEMBER(6) < SPECIAL(5) < TEAM_LEADER(4) < DIRECTOR(3) < HQ_HEAD(2) < CEO(1)
 *
 * 접근 제한 의미:
 * - maxLevel: 'DIRECTOR' → 실장 이하만 접근 가능 (본부장, 대표는 불가)
 * - maxLevel: 'UNLIMITED' → 모든 조직장 접근 가능
 */
export type JobTitleLevel =
  | 'CEO'         // 대표 (order=1)
  | 'HQ_HEAD'     // 본부장 (order=2)
  | 'DIRECTOR'    // 실장 (order=3)
  | 'TEAM_LEADER' // 팀장 (order=4)
  | 'SPECIAL'     // 담당 (order=5)
  | 'MEMBER';     // 팀원 (order=6)

/**
 * 직책 레벨 순서 (낮을수록 높은 직책)
 */
export const JOB_TITLE_LEVEL_ORDER: Record<JobTitleLevel, number> = {
  CEO: 1,
  HQ_HEAD: 2,
  DIRECTOR: 3,
  TEAM_LEADER: 4,
  SPECIAL: 5,
  MEMBER: 6,
};

/**
 * @deprecated V136.1에서 폐기됨
 * 직책명 → 레벨 매핑 (보안 취약: 문자열 기반)
 * V136.1부터 JOB_TITLE_CODE_TO_LEVEL 사용
 */
export const JOB_TITLE_TO_LEVEL: Record<string, JobTitleLevel> = {
  '대표': 'CEO',
  '본부장': 'HQ_HEAD',
  '실장': 'DIRECTOR',
  '팀장': 'TEAM_LEADER',
  '담당': 'SPECIAL',
  '팀원': 'MEMBER',
};

/**
 * 직책 코드 → 레벨 매핑 (V136.1 권장)
 * master_jobTitles.code 필드값을 레벨로 변환
 *
 * 이 매핑이 직책 레벨 판별의 단일 소스입니다.
 */
export const JOB_TITLE_CODE_TO_LEVEL: Record<string, JobTitleLevel> = {
  'CEO': 'CEO',
  'DIVISION_HEAD': 'HQ_HEAD',
  'DEPT_HEAD': 'DIRECTOR',
  'TEAM_LEADER': 'TEAM_LEADER',
  'SPECIAL': 'SPECIAL',
  'MEMBER': 'MEMBER',
};

/**
 * V136.1: 기본 직책 레벨 (최소 권한 원칙)
 *
 * jobTitleId가 없거나, master_jobTitles 조회 실패 시 적용됩니다.
 * MEMBER는 가장 낮은 레벨로, 실장 제한 메뉴에 접근할 수 없습니다.
 */
export const DEFAULT_JOB_TITLE_LEVEL: JobTitleLevel = 'MEMBER';

/**
 * 메뉴별 최대 허용 레벨 타입
 * 'UNLIMITED': 제한 없음 (모든 조직장 접근 가능)
 */
export type MenuMaxLevel = JobTitleLevel | 'UNLIMITED';

/**
 * 조직관리 메뉴 키
 */
export type OrgManagementMenuKey =
  | 'members'     // 조직원관리
  | 'projects'    // 프로젝트 관리
  | 'workStatus'  // 조직 근로현황
  | 'leave'       // 조직 휴가관리
  | 'interviews'; // 조직원면담

/**
 * 메뉴 키 → 라우트 경로 매핑
 */
export const MENU_KEY_TO_PATH: Record<OrgManagementMenuKey, string> = {
  members: '/org-management/members',
  projects: '/org-management/projects',
  workStatus: '/org-management/work-status',
  leave: '/org-management/leave',
  interviews: '/org-management/interviews',
};

/**
 * 라우트 경로 → 메뉴 키 매핑
 */
export const PATH_TO_MENU_KEY: Record<string, OrgManagementMenuKey> = {
  '/org-management/members': 'members',
  '/org-management/projects': 'projects',
  '/org-management/work-status': 'workStatus',
  '/org-management/leave': 'leave',
  '/org-management/interviews': 'interviews',
};

/**
 * 조직관리 정책 문서 스키마
 *
 * Firestore: orgManagementPolicies/default
 */
export interface OrgManagementPolicy {
  /** 메뉴별 최대 허용 직책 레벨 */
  menuMaxLevel: Record<OrgManagementMenuKey, MenuMaxLevel>;

  /** 생성일시 */
  createdAt?: any;

  /** 수정일시 */
  updatedAt?: any;
}

/**
 * 기본 정책 (초기 시드용)
 *
 * 정책 스펙:
 * - 상위조직장까지(제한 없음): 조직원관리, 프로젝트 관리
 * - 실장 단위까지만: 조직 근로현황, 조직 휴가관리, 조직원면담
 */
export const DEFAULT_ORG_MANAGEMENT_POLICY: OrgManagementPolicy = {
  menuMaxLevel: {
    members: 'UNLIMITED',     // 조직원관리: 제한 없음
    projects: 'UNLIMITED',    // 프로젝트 관리: 제한 없음
    workStatus: 'DIRECTOR',   // 조직 근로현황: 실장까지만
    leave: 'DIRECTOR',        // 조직 휴가관리: 실장까지만
    interviews: 'DIRECTOR',   // 조직원면담: 실장까지만
  },
};

/**
 * 사용자 직책 레벨이 특정 메뉴에 접근 가능한지 확인
 *
 * V136.1: 기본값을 MEMBER로 변경 (최소 권한 원칙)
 *
 * @param userLevel 사용자 직책 레벨
 * @param maxLevel 메뉴의 최대 허용 레벨
 * @returns 접근 가능 여부
 */
export function canAccessByLevel(
  userLevel: JobTitleLevel | undefined,
  maxLevel: MenuMaxLevel
): boolean {
  // 제한 없음이면 무조건 허용
  if (maxLevel === 'UNLIMITED') {
    return true;
  }

  // V136.1: 사용자 레벨이 없으면 MEMBER로 처리 (최소 권한)
  // MEMBER(order=6) >= DIRECTOR(order=3) → false (접근 불가)
  const effectiveLevel = userLevel || DEFAULT_JOB_TITLE_LEVEL;

  const userOrder = JOB_TITLE_LEVEL_ORDER[effectiveLevel];
  const maxOrder = JOB_TITLE_LEVEL_ORDER[maxLevel];

  // userOrder가 maxOrder보다 크거나 같으면 접근 가능
  // (예: 팀장=4 >= 실장=3 → 접근 가능)
  // (예: 본부장=2 >= 실장=3 → 접근 불가)
  // (예: MEMBER=6 >= 실장=3 → 접근 불가) ← V136.1 기본값
  return userOrder >= maxOrder;
}

/**
 * @deprecated V136.1에서 폐기됨
 * 사용자 직책명에서 레벨 추출 (보안 취약)
 * V136.1부터 getJobTitleLevelByCode 사용
 *
 * @param jobTitle users.jobTitle 필드값 (예: "팀장", "실장")
 * @returns JobTitleLevel 또는 undefined
 */
export function getJobTitleLevel(jobTitle: string | undefined): JobTitleLevel | undefined {
  if (!jobTitle) return undefined;
  return JOB_TITLE_TO_LEVEL[jobTitle];
}

/**
 * V136.1: 직책 코드에서 레벨 추출 (권장)
 *
 * master_jobTitles.code를 기반으로 직책 레벨을 판별합니다.
 * 매핑되지 않는 코드는 undefined를 반환하며,
 * 호출자는 DEFAULT_JOB_TITLE_LEVEL을 fallback으로 사용해야 합니다.
 *
 * @param code master_jobTitles.code 필드값 (예: "TEAM_LEADER", "DEPT_HEAD")
 * @returns JobTitleLevel 또는 undefined
 */
export function getJobTitleLevelByCode(code: string | undefined): JobTitleLevel | undefined {
  if (!code) return undefined;
  return JOB_TITLE_CODE_TO_LEVEL[code];
}
